/**
 * Background geofencing, Android and iOS — the app's half (Android: card
 * `t_23f46db0`, plan `C:/Projects/Otto/docs/plans/GEOFENCING-E2E.md` §2.4;
 * iOS: card `t_e156b106`, plan `C:/Projects/Otto/docs/plans/GEOFENCING-IOS.md`
 * §2.4; the account-wide set: card `t_59300f4d`).
 *
 * The cloud owns the areas; the OS owns the monitoring — Play Services'
 * `GeofencingClient` on Android, Core Location's `CLLocationManager` on iOS
 * (the Swift twin built in-app by card I1, same jsName, same method shapes).
 * This module is the bridge between cloud and OS on both platforms, and it
 * exists because a phone's foreground tracker
 * (`composables/useLocationTracking.ts`) stops when the app is closed — an
 * OS-registered geofence keeps firing transitions with the app dead, which is
 * the whole point of the feature (owner decision Q3).
 *
 * What it does, in one line: **pull the ACCOUNT's areas — the union across
 * every hub it holds (card `t_59300f4d`) — turn them into one set of OS regions
 * inside the platform's own limits (`osLimitsFor()`: Play Services' 100 / 100 m
 * on Android, Apple's 20 regions / 200 m on iOS), and hand them to the native
 * plugin** — then keep the two in step as the account and the opt-in change,
 * and take the regions away when either goes. One set, deduped by area id: the
 * cloud's area ids are uuids (`GeofenceArea.id`), so `area:<cloudId>` names one
 * area across all hubs — the union never collides by id, however the area
 * *names* repeat between households.
 *
 * The union read is the primary pull, never the only one (card `t_f1beda2a`): a
 * cloud that predates `GET /presence/areas/all` answers 404 there, and a shipped
 * app against a cloud that has not been deployed yet is the normal state — so a
 * failed union read degrades to the pre-union per-hub read for the hub in scope,
 * and only a failure of BOTH leaves the OS untouched (fail closed, nothing
 * cleared). The sync's own line names the path that served (`source=all|hub`).
 *
 * Three rules it is built to, all decided (plan §2.4 / Q4, card guards):
 *
 *  - **Consent gates registration, fail closed.** OS-registered geofences are
 *    background collection; no recorded opt-in means no registration, and a
 *    revocation unregisters (`removeAll`). The opt-in is the same one A1's
 *    cloud report uses (`services/presenceConsent.ts`), read fresh so a change
 *    made on another client is honoured.
 *  - **Sign-out unregisters.** No cloud session, no OS geofences either.
 *  - **Re-register only when the set actually changed.** The OS documents that
 *    Play Services' geofences survive its restarts and only need re-registering
 *    after a reboot, an app update/reinstall, or cleared data. iOS promises no
 *    such thing: Apple documents that the system monitors regions "until …
 *    the device reboots" and does not state that they are restored across one
 *    (card I5's finding F1), and the iOS-15-era bug class drops monitoring
 *    silently — so the iOS plugin answers `getRegistered()` from the OS's own
 *    `monitoredRegions` (composed with its record) instead of the record
 *    alone, which makes a loss fail this comparison instead of hiding behind
 *    it; the native monitor also re-registers the record at launch and on
 *    app-active (card `t_701abf82`). A sync compares what it wants with what
 *    the device actually holds and does nothing when they match
 *    (`regionsEqual`, `utils/geofenceRegions.ts`). That is also what makes it
 *    safe to call on every sign-in, foreground and area edit.
 *
 * Where the wiring lives: `initBackgroundGeofenceSync()` is called once from
 * `App.vue`'s setup, beside the push-registration watcher — the app's one "the
 * account changed" hook. Everything it schedules reuses this module, and
 * `syncBackgroundGeofences()` is exported for the areas UI (card A4: re-sync
 * after an area is created, edited or deleted).
 *
 * iOS is in scope as of the iOS lane (cards I1/I3, plan `GEOFENCING-IOS.md`):
 * the same JS drives the Swift plugin under the same jsName with Apple's limits
 * from `osLimitsFor()`, and `isSupported` stays the one gate that keeps every
 * path quiet on platforms that are neither (the web build).
 */
import { watch } from 'vue';
import {
  buildRegions,
  osLimitsFor,
  regionBuildLine,
  regionsEqual,
  type NativeGeofencePlatform,
  type OsGeofenceRegion,
  type RegionBuild,
} from '../utils/geofenceRegions';
import { presenceConsent, presenceConsentGranted } from './presenceConsent';
import { recordGeofenceSyncResult } from './presenceDiagnostics';

// ── The native plugin's shape (the Kotlin twin of this file) ────────────────

/** What `addRegions` answers — the counts and every change it made. */
export interface GeofenceAddResult {
  registered: number;
  requested: number;
  clamped: Array<{ id: string; from: number; to: number }>;
  skipped: Array<{ id: string; reason: string }>;
  maxRegions: number;
}

/** One transition the OS delivered (the durable queue and the live event). */
export interface GeofenceTransition {
  /** Deterministic (`area:<id>:<type>:<at>`), so a replayed delivery is one event. */
  eventId: string;
  /** The region id, i.e. `area:<cloud area id>`. */
  id: string;
  areaId: string;
  type: 'entered' | 'left' | 'dwell';
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  /** ISO-8601, from the transition the OS reported. */
  at: string;
}

export interface GeofencePermissionState {
  /** `ACCESS_FINE_LOCATION` (+ coarse). */
  location: string;
  /** `ACCESS_BACKGROUND_LOCATION`, or `granted` below API 29 where it is implicit. */
  backgroundLocation: string;
  /** True when the plugin may register geofences right now. */
  canRegister: boolean;
  /** True when the platform wants the user sent to app settings (Android 11+). */
  needsSettings?: boolean;
}

/**
 * The subset of the plugin this module uses. Declared as an interface so the
 * sync is testable without a device: the Kotlin plugin in
 * `capacitor-plugins/android-background-geofence` implements exactly this, and
 * the Swift twin (card I1, `ios/App/App/BackgroundGeofence/`) answers the same
 * methods under the same jsName.
 */
export interface BackgroundGeofencePluginLike {
  addRegions(options: { regions: OsGeofenceRegion[] }): Promise<GeofenceAddResult>;
  removeAll(): Promise<{ removed: number }>;
  /**
   * The OS's registered regions. iOS answers the OS's own set — the ids from
   * `CLLocationManager.monitoredRegions`, radius/coordinates/notes from its
   * record — so a silent OS-side loss (a reboot, the iOS-15-era bug class)
   * fails the sync's same-set fast path; Android answers its record, as its
   * twin documents (card `t_701abf82`).
   */
  getRegistered(): Promise<{ regions: OsGeofenceRegion[] }>;
  getTransitions(): Promise<{ transitions: GeofenceTransition[] }>;
  /**
   * Clear the queue. No arguments clears everything (A2's shape); `eventIds`
   * removes exactly those entries (card A3 — a flush that partially succeeded
   * leaves the rest queued instead of dropping or re-posting them).
   */
  clearTransitions(options?: { eventIds?: string[] }): Promise<{ cleared: number }>;
  checkPermissions?(): Promise<GeofencePermissionState>;
  requestPermissions?(options?: { permissions?: string[] }): Promise<GeofencePermissionState>;
  openAppSettings?(): Promise<{ opened: boolean }>;
  addListener?(
    eventName: 'transition',
    listener: (transition: GeofenceTransition) => void,
  ): Promise<{ remove: () => Promise<void> }> | { remove: () => Promise<void> };
}

// ── Sync ───────────────────────────────────────────────────────────────────

export interface BackgroundGeofenceDeps {
  /** `authStore.isCloudAuthenticated` — no account, no background collection. */
  isCloudAuthenticated: () => boolean;
  /** The plan-Q4 opt-in; read fresh so a revocation stops the monitoring. */
  hasConsent: () => boolean | Promise<boolean>;
  /** The native geofencing platforms only (`android`, `ios`) — anything else is a quiet skip. */
  isSupported: () => boolean;
  /**
   * The native platform this build runs on — picks the OS limit pair the build
   * applies (`osLimitsFor`, `utils/geofenceRegions.ts`). Optional, and
   * `android` when absent, only because the Android-era tests build their deps
   * without it; the running app (`runtimeDeps`) always answers, and only after
   * `isSupported` said yes.
   */
  platform?: () => NativeGeofencePlatform;
  /**
   * The PRIMARY pull — `cloudFetch` → `GET /presence/areas/all` (card
   * `t_59300f4d`): every area of every hub the account holds, each row
   * attributed to its hub (`hubName`, `cloudHubId`). The union is what the
   * phone installs while the cloud serves it, so a walk past ANY of the
   * account's places wakes the app, whichever hub serves it.
   */
  fetchAllAreas: () => Promise<unknown>;
  /**
   * The FALLBACK's half one (card `t_f1beda2a`): the cloud row id (`hubs.id`)
   * of the hub in scope, or `null` when there is none — the pre-union
   * resolution, and its `null` still answers the pre-union "no hub in scope"
   * skip. Consulted only when the union read fails: a cloud older than
   * `/presence/areas/all` answers 404 there, and version skew between a
   * shipped app and the cloud is the normal state, not an error.
   */
  resolveHubId: () => string | null | Promise<string | null>;
  /**
   * The FALLBACK's read (card `t_f1beda2a`): `cloudFetch` →
   * `GET /presence/areas?hubId=…` — the pre-union pull, the same wire shape it
   * always had, serving the hub in scope when the union route is not there.
   * The areas manager UI reads through the same path
   * (`services/cloudGeofenceAreas.ts`).
   */
  fetchAreas: (cloudHubId: string) => Promise<unknown>;
  plugin: BackgroundGeofencePluginLike;
  log?: (message: string, error?: unknown) => void;
}

export type GeofenceSyncOutcome = 'registered' | 'unchanged' | 'cleared' | 'skipped' | 'failed';

export interface GeofenceSyncResult {
  outcome: GeofenceSyncOutcome;
  /** Regions now registered with the OS after this sync. */
  registered?: number;
  /** Why a sync skipped/cleared/failed — a short machine word, always present. */
  reason?: string;
  /** The build behind a `registered`/`unchanged` answer. */
  build?: RegionBuild;
}

function logger(deps: BackgroundGeofenceDeps): (message: string, error?: unknown) => void {
  return deps.log ?? ((message: string, error?: unknown) => console.info(message, error ?? ''));
}

/**
 * The cloud areas path of ONE hub, relative to the cloud API base — the areas
 * manager UI's read (`services/cloudGeofenceAreas.ts`), unchanged by the
 * account-wide sync.
 */
export function presenceAreasPath(cloudHubId: string): string {
  return `/presence/areas?hubId=${encodeURIComponent(cloudHubId)}`;
}

/**
 * The account-wide areas path (card `t_59300f4d`): every area of every hub the
 * account holds, one read — the pull the background sync makes.
 */
export function presenceAllAreasPath(): string {
  return '/presence/areas/all';
}

/**
 * Which read served a sync's areas (card `t_f1beda2a`): the account-wide union
 * (`all`) or the pre-union per-hub fallback (`hub`). The sync's own log line
 * carries it as `source=<word>`, so a log reader can tell which path ran.
 */
type GeofenceAreaSource = 'all' | 'hub';

/**
 * Bring the OS's registered regions in line with the cloud's areas — every hub
 * the account holds (the union, card `t_59300f4d`), registered as ONE set.
 * Total by contract — every end answers a `GeofenceSyncOutcome`, and a failure
 * (the cloud, the plugin) is logged and answered rather than thrown, so a
 * caller inside a watcher can never be broken by it.
 *
 * Every answer is handed to the presence page's diagnostic record
 * (`services/presenceDiagnostics.ts`, card `t_9ec98b5c`) — observation only,
 * after the sync has already decided, so nothing about the sync moves.
 */
export async function syncBackgroundGeofences(deps: BackgroundGeofenceDeps): Promise<GeofenceSyncResult> {
  const result = await runBackgroundGeofenceSync(deps);
  recordGeofenceSyncResult(result);
  return result;
}

/** The sync itself; `syncBackgroundGeofences` is the recording entry point. */
async function runBackgroundGeofenceSync(deps: BackgroundGeofenceDeps): Promise<GeofenceSyncResult> {
  const log = logger(deps);

  try {
    if (!deps.isSupported()) {
      return { outcome: 'skipped', reason: 'unsupported-platform' };
    }

    if (!deps.isCloudAuthenticated()) {
      const removed = await deps.plugin.removeAll();
      if (removed.removed > 0) log(`[BackgroundGeofence] signed out — removed ${removed.removed} region(s)`);
      return { outcome: 'cleared', registered: 0, reason: 'signed-out' };
    }

    if (!(await deps.hasConsent())) {
      const removed = await deps.plugin.removeAll();
      if (removed.removed > 0) log(`[BackgroundGeofence] opted out — removed ${removed.removed} region(s)`);
      return { outcome: 'cleared', registered: 0, reason: 'no-consent' };
    }

    // The OS limit pair of the platform this build is on: Play Services'
    // 100 / 100 m or Apple's 20 regions / 200 m floor (card `t_e156b106`,
    // `osLimitsFor`). Deps built without a platform keep Android's pair.
    const platform = deps.platform?.() ?? 'android';
    const limits = osLimitsFor(platform);

    // The primary read — ONE pull for the whole account (card `t_59300f4d`):
    // the union of every hub's areas, merged by `buildRegions` (dedupe by area
    // id, radius floor, region cap). A phone carries ALL the account's places,
    // not only the hub in scope — and never the areas of a hub the account does
    // not hold.
    //
    // A union read that FAILS is version skew before it is a failure (card
    // `t_f1beda2a`): a cloud that predates the route answers 404, and an app
    // build against a cloud that has not been deployed yet is the normal state
    // — so the sync degrades to the pre-union per-hub read instead of failing
    // closed, which would leave the OS holding nothing, silently. The fallback
    // is the old behaviour exactly: the hub in scope resolved, its `null` ⇒ the
    // old "no hub in scope" skip, a failing per-hub read ⇒ `failed` with
    // nothing touched (it reaches the catch below). Only a union that ANSWERS —
    // even with no areas — is trusted; a failure never clears anything.
    let build: RegionBuild;
    let source: GeofenceAreaSource = 'all';
    let hubId: string | null = null;
    try {
      build = buildRegions(await deps.fetchAllAreas(), limits);
    } catch (error) {
      log('[BackgroundGeofence] the account-wide areas read failed — falling back to the hub in scope:', error);
      hubId = await deps.resolveHubId();
      if (!hubId) {
        // No hub in scope is not "no areas": the regions already registered
        // stay (a launcher visit must not stop the monitoring) — the pre-union
        // rule, kept as it was.
        log('[BackgroundGeofence] no hub in scope — nothing to register (source=hub)');
        return { outcome: 'skipped', reason: 'no-hub-in-scope' };
      }
      build = buildRegions(await deps.fetchAreas(hubId), limits);
      source = 'hub';
    }

    // What the messages name and what the line says served: the account (the
    // union read) or the hub the fallback read.
    const scope = source === 'all' ? 'the account' : `hub ${hubId}`;

    if (!build.regions.length) {
      const removed = await deps.plugin.removeAll();
      log(`[BackgroundGeofence] ${scope} has no areas — removed ${removed.removed} region(s) (source=${source})`);
      return { outcome: 'cleared', registered: 0, reason: 'no-areas', build };
    }

    const registered = await deps.plugin.getRegistered();
    if (regionsEqual(build.regions, registered.regions ?? [])) {
      return { outcome: 'unchanged', registered: build.regions.length, reason: 'same-set', build };
    }

    const added = await deps.plugin.addRegions({ regions: build.regions });
    log(
      `[BackgroundGeofence] registered ${added.registered}/${added.requested} region(s) for ${scope} — ` +
        `source=${source} ${regionBuildLine(build)}`,
    );
    for (const clamp of build.clamped) {
      log(`[BackgroundGeofence] ${clamp.id}: radius ${clamp.from} m → ${clamp.to} m (OS minimum)`);
    }
    for (const drop of build.dropped) {
      log(`[BackgroundGeofence] ${drop.id || '(no id)'}: not registered (${drop.reason})`);
    }

    return { outcome: 'registered', registered: added.registered, reason: 'changed', build };
  } catch (error) {
    log('[BackgroundGeofence] sync failed:', error);
    return { outcome: 'failed', reason: 'error' };
  }
}

/** Take every OS region away (sign-out, opt-out, or a surface that wants none). */
export async function clearBackgroundGeofences(deps: BackgroundGeofenceDeps): Promise<GeofenceSyncResult> {
  const log = logger(deps);
  try {
    if (!deps.isSupported()) return { outcome: 'skipped', reason: 'unsupported-platform' };
    const removed = await deps.plugin.removeAll();
    return { outcome: 'cleared', registered: 0, reason: `removed-${removed.removed}` };
  } catch (error) {
    log('[BackgroundGeofence] clear failed:', error);
    return { outcome: 'failed', reason: 'error' };
  }
}

// ── Runtime wiring (the app's own dependencies) ─────────────────────────────

/**
 * The native platform this build runs on, when it is one whose OS geofencing
 * this app drives (card `t_e156b106`): `'android'` behind the Kotlin plugin,
 * `'ios'` behind the Swift twin (card I1, same jsName). `null` everywhere else
 * — the web build, another native platform, or a Capacitor global that throws.
 */
function nativeGeofencePlatform(): NativeGeofencePlatform | null {
  try {
    const capacitor = window.Capacitor;
    if (!capacitor?.isNativePlatform?.()) return null;
    const platform = capacitor?.getPlatform?.();
    return platform === 'android' || platform === 'ios' ? platform : null;
  } catch {
    return null;
  }
}

/**
 * Whether this build may touch OS geofencing at all — the gate behind
 * `isSupported`, answering for both native platforms this app drives (Android
 * today, iOS as of cards I1/I2/I3) and nothing else. Exported because the
 * module's own test pins the truth table through it.
 */
export function isNativeGeofencePlatform(): boolean {
  return nativeGeofencePlatform() !== null;
}

/**
 * The native geofencing platform this build runs on — `'android'` or `'ios'`,
 * `null` on anything else. The sync takes the OS limit pair off the same answer
 * internally; the presence page's diagnostic block reads it to name the cap and
 * the radius floor it shows (card `t_9ec98b5c`).
 */
export function currentGeofencePlatform(): NativeGeofencePlatform | null {
  return nativeGeofencePlatform();
}

/**
 * The regions the OS is holding, straight from the plugin — on iOS this is the
 * OS's own monitored set, not the record (card `t_701abf82`). Read-only, for
 * surfaces and probes: this is the function a device check calls.
 */
export async function registeredGeofences(): Promise<OsGeofenceRegion[]> {
  await ensureNativePlugin();
  const registered = await nativePlugin().getRegistered();
  return registered.regions ?? [];
}

/**
 * Load the plugin handle, if it is not loaded yet. Split from the getter below
 * ON PURPOSE — and the split is load-bearing (card `t_064a3c4e`).
 *
 * A Capacitor plugin object is a `Proxy` whose `get` trap wraps EVERY property,
 * `then` included, so the handle must never be an async function's VALUE: the
 * promise resolution procedure would see a thenable, call `then(...)`, and
 * Capacitor answers `"<plugin>.then()" is not implemented on android`. Measured
 * again on the rig (emulator, API 34, 2026-10-02): an `async function
 * backgroundGeofencePlugin()` reintroduced exactly that error on the app's
 * startup path — the same trap A2 fixed in the loader one commit earlier, which
 * is why the pair below is the only shape that may hand this handle out.
 */
export async function loadBackgroundGeofencePlugin(): Promise<void> {
  await ensureNativePlugin();
}

/**
 * The plugin handle itself — the relay (card A3) drains the same durable queue
 * through it (`getTransitions` / `clearTransitions({eventIds})`), and one handle
 * is one plugin instance however many callers share it. Sync on purpose: only
 * valid after `loadBackgroundGeofencePlugin()` has resolved.
 */
export function backgroundGeofencePlugin(): BackgroundGeofencePluginLike {
  return nativePlugin();
}

/**
 * The plugin handle, held in a plain variable — and this shape is load-bearing.
 *
 * A Capacitor plugin object is a `Proxy` whose `get` trap turns EVERY property
 * into a callable wrapper, `then` included. A promise that resolves *to* that
 * object therefore never settles: the resolution procedure sees a thenable,
 * calls `then(...)`, and Capacitor answers `"<plugin>.then()" is not implemented
 * on android`. Measured on the rig build (emulator-5554, 2026-10-02): the launch
 * path died exactly there, before any geofence was registered.
 *
 * So the proxy is never a promise's value: the loader returns nothing, and the
 * handle is read out of module state afterwards.
 */
let pluginRef: BackgroundGeofencePluginLike | null = null;

async function ensureNativePlugin(): Promise<void> {
  if (pluginRef) return;
  const { registerPlugin } = await import('@capacitor/core');
  pluginRef = registerPlugin<BackgroundGeofencePluginLike>('BackgroundGeofence', {
    web: () => Promise.resolve(webFallback),
  });
}

function nativePlugin(): BackgroundGeofencePluginLike {
  if (!pluginRef) throw new Error('the BackgroundGeofence plugin has not been loaded');
  return pluginRef;
}

/**
 * What a non-native build answers. The sync is gated off on those platforms
 * before it ever gets here (`isSupported`), so this exists to make an
 * unintended call loud instead of silent.
 */
const webFallback: BackgroundGeofencePluginLike = {
  addRegions: async () => {
    throw new Error('Background geofencing is only implemented on Android and iOS');
  },
  removeAll: async () => ({ removed: 0 }),
  getRegistered: async () => ({ regions: [] }),
  getTransitions: async () => ({ transitions: [] }),
  clearTransitions: async () => ({ cleared: 0 }),
};

/** The dependencies the running app supplies; tests inject their own. */
async function runtimeDeps(): Promise<BackgroundGeofenceDeps> {
  const [
    { useAuthStore },
    { cloudFetch },
    { readPresenceConsent },
    { loadAccountHubs, findAccountHubRow },
    { getActiveHubOwnId },
  ] = await Promise.all([
    import('../stores/auth'),
    import('./cloudApi'),
    import('./presenceConsent'),
    import('./accountHubs'),
    import('../config/runtime'),
  ]);
  await ensureNativePlugin();

  return {
    isCloudAuthenticated: () => useAuthStore().isCloudAuthenticated,
    isSupported: isNativeGeofencePlatform,
    // Read only after `isSupported` answered yes, so this always names a real
    // native platform; the fallback satisfies the type, not the runtime.
    platform: () => nativeGeofencePlatform() ?? 'android',
    hasConsent: async () => {
      // Re-read: the opt-in can be revoked from another client, and this is the
      // reader that moves the gate (fail-closed in `presenceConsent`).
      await readPresenceConsent();
      return presenceConsentGranted();
    },
    // One read for the whole account (card `t_59300f4d`): every hub it holds,
    // every area of each — the union the phone installs as OS regions. No hub
    // id is resolved or sent on this path: the cloud answers the account's
    // whole set from the token alone.
    fetchAllAreas: () => cloudFetch(presenceAllAreasPath()),
    // The version-skew fallback (card `t_f1beda2a`) — the pre-union wiring,
    // restored beside the union: a cloud that does not serve
    // `/presence/areas/all` yet answers 404, and the sync then pulls the hub in
    // scope rather than arming nothing. One hub, two id spaces
    // (config/runtime.ts): the window records the hub's OWN id, while the
    // cloud's area route speaks its row id — the account's hub list is the
    // translation, and `findAccountHubRow` accepts either spelling. Consulted
    // only when the union read fails; the union stays the primary pull.
    resolveHubId: async () => {
      await loadAccountHubs();
      const ownId = getActiveHubOwnId();
      if (!ownId) return null;
      const row = findAccountHubRow(ownId);
      return row?.id ?? null;
    },
    fetchAreas: (hubId: string) => cloudFetch(presenceAreasPath(hubId)),
    plugin: nativePlugin(),
  };
}

/** `syncBackgroundGeofences` against the running app's real dependencies. */
export async function syncRegisteredBackgroundGeofences(): Promise<GeofenceSyncResult> {
  return syncBackgroundGeofences(await runtimeDeps());
}

/** `clearBackgroundGeofences` against the running app's real dependencies. */
export async function clearRegisteredBackgroundGeofences(): Promise<GeofenceSyncResult> {
  return clearBackgroundGeofences(await runtimeDeps());
}

/** How long a foreground re-sync waits after the previous one (see below). */
export const FOREGROUND_SYNC_MIN_INTERVAL_MS = 5 * 60_000;

let installed = false;
let lastSyncAt = 0;

/**
 * Install the app's watchers, once. Called from `App.vue`'s setup:
 *
 *  - the **account** decides registration (sign in → sync, sign out → clear);
 *  - the **opt-in** decides it too (`presenceConsent` is A1's live ref — a
 *    `true` registers, a `false` unregisters, and `null`, "not read yet", does
 *    nothing at all);
 *  - coming back to the foreground re-syncs, at most once per
 *    `FOREGROUND_SYNC_MIN_INTERVAL_MS` — the cheap moment to pick up an area
 *    someone else's session just edited, without hammering the cloud on every
 *    app switch. (The OS write itself is skipped unless the set changed.)
 *
 * It also drives the A3 relay, on the same two moments and for the same reason:
 * a sign-in and a foreground are when the app holds a token it can refresh, so
 * they are when (a) the receiver's stored credentials are re-stamped and (b)
 * the transitions a closed app could not deliver are flushed to the cloud. The
 * flush is NOT throttled with the sync: with an empty queue it costs one local
 * plugin read and answers, and a queue is exactly what must not wait.
 *
 * Area edits inside this app do not need a watcher: the areas UI (A4) calls
 * `syncRegisteredBackgroundGeofences()` after a change, which is the one
 * surface that knows a change happened.
 */
export async function initBackgroundGeofenceSync(): Promise<void> {
  if (installed) return;
  installed = true;

  const { useAuthStore } = await import('../stores/auth');
  const authStore = useAuthStore();
  const deps = await runtimeDeps();

  const runSync = (): void => {
    lastSyncAt = Date.now();
    void syncBackgroundGeofences(deps);
  };

  /**
   * Re-stamp the receiver's credentials and drain what it could not deliver.
   * Persist first: it is what renews a near-expiry token, so the drain posts
   * with the freshest one the app has.
   */
  const runRelay = (): void => {
    void (async () => {
      const { persistPresenceRelayCredentials, flushPresenceRelayQueue } = await import('./presenceRelay');
      await persistPresenceRelayCredentials();
      await flushPresenceRelayQueue();
    })();
  };

  /** Sign-out / opt-out: the receiver must not post, and the queue belongs to the session that made it. */
  const endRelay = (): void => {
    void (async () => {
      const { stopPresenceRelay } = await import('./presenceRelay');
      await stopPresenceRelay();
    })();
  };

  watch(
    () => authStore.isCloudAuthenticated,
    (authenticated) => {
      if (authenticated) {
        runSync();
        runRelay();
      } else {
        void clearBackgroundGeofences(deps);
        endRelay();
      }
    },
    { immediate: true },
  );

  watch(presenceConsent, (consent) => {
    if (consent === true) {
      runSync();
      runRelay();
    } else if (consent === false) {
      void clearBackgroundGeofences(deps);
      endRelay();
    }
    // `null` — nothing has been read from the hub yet. Fail closed is about
    // registering nothing new, not about tearing down what is already there.
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) return;
    if (Date.now() - lastSyncAt >= FOREGROUND_SYNC_MIN_INTERVAL_MS) runSync();
    runRelay();
  });
}

/** Test seam: forget that the watchers were installed. */
export function resetBackgroundGeofenceSyncForTests(): void {
  installed = false;
  lastSyncAt = 0;
  pluginRef = null;
}

/**
 * Test seam: install the plugin handle the runtime paths build through
 * `registerPlugin`. A Node test cannot load the Capacitor runtime, and the
 * promise-shape rule above is exactly what a test must be able to exercise
 * without it.
 */
export function setBackgroundGeofencePluginForTests(plugin: BackgroundGeofencePluginLike | null): void {
  pluginRef = plugin;
}
